import { useState } from "react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Wrench, CheckCircle } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitServiceBooking } from "@/lib/api";

const SERVICE_TYPES = [
  { value: "general", label: "Γενικό Σέρβις" },
  { value: "major", label: "Μεγάλο / 20K Σέρβις" },
  { value: "tires", label: "Ελαστικά & Τροχοί" },
  { value: "diagnostics", label: "Διάγνωση & ECU" },
  { value: "custom", label: "Custom / Tuning" },
];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  bike_brand: "",
  bike_model: "",
  bike_year: "",
  service_type: "general",
  notes: "",
};

export default function ServiceBooking() {
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Διάλεξε ημερομηνία");
      return;
    }
    if (!form.full_name || !form.email || !form.phone || !form.bike_brand || !form.bike_model || !form.bike_year) {
      toast.error("Συμπλήρωσε όλα τα υποχρεωτικά πεδία");
      return;
    }
    setSubmitting(true);
    try {
      await submitServiceBooking({
        ...form,
        bike_year: Number(form.bike_year),
        preferred_date: format(date, "yyyy-MM-dd"),
        service_type: SERVICE_TYPES.find(s => s.value === form.service_type)?.label || form.service_type,
      });
      toast.success("Το ραντεβού σέρβις παραλήφθηκε. Θα επιβεβαιώσουμε σύντομα.");
      setSuccess(true);
      setForm(initialForm);
      setDate(undefined);
    } catch (err) {
      toast.error("Αποτυχία υποβολής. Δοκίμασε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="service-page" className="pt-40 md:pt-52 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-overline mb-4">Εργαστήριο</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl leading-[0.9]">
            Κλείσε<br/>
            <span className="text-[#E50000]">ραντεβού σέρβις.</span>
          </h1>
          <p className="mt-6 text-white/65 leading-relaxed">
            Πες μας για τη μηχανή σου και τι χρειάζεται. Οι τεχνικοί μας θα επιβεβαιώσουν το ραντεβού εντός 24 ωρών.
          </p>

          <ul className="mt-10 space-y-4 text-sm text-white/70">
            {[
              "Εργοστασιακά εκπαιδευμένοι σε 8 μάρκες",
              "Προγραμματισμός ECU σε δυναμόμετρο",
              "Τοποθέτηση & ζυγοστάθμιση ελαστικών",
              "Έλεγχος πριν το ΚΤΕΟ"
            ].map((it,i)=>(
              <li key={i} className="flex items-start gap-3 border-l-2 border-[#E50000] pl-4 py-1">
                <span className="font-display text-white">{it}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div data-testid="service-success" className="border border-white/10 bg-[#141414] p-10 text-center">
              <CheckCircle size={48} weight="duotone" className="text-[#E50000] mx-auto" />
              <h2 className="font-display font-black uppercase text-3xl mt-6">Το ραντεβού παραλήφθηκε</h2>
              <p className="mt-3 text-white/60">Η ομάδα του εργαστηρίου θα επικοινωνήσει για να επιβεβαιώσει το ραντεβού.</p>
              <button data-testid="service-new-booking-btn" onClick={() => setSuccess(false)} className="btn-outline text-xs mt-8">
                Νέο ραντεβού
              </button>
            </div>
          ) : (
            <form
              data-testid="service-booking-form"
              onSubmit={onSubmit}
              className="border border-white/10 bg-[#141414] p-8 md:p-10 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Ονοματεπώνυμο" required>
                  <input data-testid="service-name-input" required type="text" value={form.full_name} onChange={update("full_name")} className="form-input" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="service-email-input" required type="email" value={form.email} onChange={update("email")} className="form-input" />
                </Field>
                <Field label="Τηλέφωνο" required>
                  <input data-testid="service-phone-input" required type="tel" value={form.phone} onChange={update("phone")} className="form-input" />
                </Field>
                <Field label="Τύπος Σέρβις" required>
                  <Select value={form.service_type} onValueChange={(v) => setForm(f => ({...f, service_type: v}))}>
                    <SelectTrigger data-testid="service-type-trigger" className="form-input h-auto py-3 justify-between">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border border-white/15 text-white rounded-none">
                      {SERVICE_TYPES.map(s => (
                        <SelectItem key={s.value} value={s.value} className="focus:bg-[#E50000] focus:text-white">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                <Field label="Μάρκα Μηχανής" required>
                  <input data-testid="service-bike-brand-input" required type="text" value={form.bike_brand} onChange={update("bike_brand")} className="form-input" placeholder="π.χ. Ducati" />
                </Field>
                <Field label="Μοντέλο" required>
                  <input data-testid="service-bike-model-input" required type="text" value={form.bike_model} onChange={update("bike_model")} className="form-input" placeholder="π.χ. Panigale V4" />
                </Field>
                <Field label="Έτος" required>
                  <input data-testid="service-bike-year-input" required type="number" min="1970" max="2030" value={form.bike_year} onChange={update("bike_year")} className="form-input" placeholder="2024" />
                </Field>
              </div>

              <Field label="Επιθυμητή Ημερομηνία" required>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="service-date-trigger"
                      className="form-input flex items-center justify-between text-left"
                    >
                      <span className={date ? "text-white" : "text-white/40"}>
                        {date ? format(date, "PPP", { locale: el }) : "Επίλεξε ημερομηνία"}
                      </span>
                      <CalendarIcon size={18} className="text-[#E50000]" weight="bold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#141414] border-white/15 rounded-none" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                      locale={el}
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Σημειώσεις">
                <textarea data-testid="service-notes-input" rows={4} value={form.notes} onChange={update("notes")} className="form-input resize-none" placeholder="Κάποιο συγκεκριμένο θέμα, θόρυβος, κωδικός βλάβης..." />
              </Field>

              <button
                data-testid="service-submit-btn"
                disabled={submitting}
                type="submit"
                className="btn-primary text-xs w-full md:w-auto disabled:opacity-50"
              >
                <Wrench size={14} weight="bold" className="mr-2" />
                {submitting ? "Υποβολή..." : "Υποβολή Κράτησης"}
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #1A1A1A;
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          padding: 0.85rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
          border-radius: 2px;
        }
        .form-input:focus { border-color: #E50000; }
        .form-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}{required && <span className="text-[#E50000]"> *</span>}
      </span>
      {children}
    </label>
  );
}
